<template>
  <div class="label-wrapper my-1">
    <span
      :id="idAttribute"
      ref="label"
      class="label d-inline-block py-1 px-2 text-center rounded-pill"
      :class="{ selected: selected }"
      @click.stop.prevent="selectCabin"
    >
      {{ label }}
    </span>

    <div 
      ref="svgContainer"
      class="svgContainer"
    >
      <svg
        ref="svg"
        width="0"
        height="0"
        :class="{ selected: selected }"
      >
        <path
          ref="svgPath"
          d="M0 0"
          :stroke="color()"
          fill="none"
          stroke-width="1px"
        />
      </svg>
    </div>
  </div>
</template>

<script lang="ts">
import { $cabin } from '@/scss/expinet'
import { Cabin } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { PropType, defineComponent } from 'vue'

// helper functions, it turned out chrome doesn't support Math.sgn()
const absolute = (x: number) => {
  return x < 0 ? -x : x
}

const getOffset = (el: HTMLElement) => {
  const domRect = el.getBoundingClientRect()
  return {
    // IE does not support scrollX etc.
    left: domRect.left + (window.scrollX ? window.scrollX : window.pageXOffset),
    top: domRect.top + (window.scrollY ? window.scrollY : window.pageYOffset),
  }
}

const drawPath = (svg: HTMLElement | HTMLCanvasElement, path: HTMLElement, startX: number, startY: number, endX: number, endY: number) => {
  // get the path's stroke width (if one wanted to be  really precize, one could use half the stroke size)
  const stroke = path.getAttribute('stroke-width') === null ? 0 : parseFloat(path.getAttribute('stroke-width')!)
  // check if the svg is big enough to draw the path, if not, set heigh/width
  if (svg.getAttribute('height') !== null && parseInt(svg.getAttribute('height')!, 10) < endY) svg.setAttribute('height', endY.toString())

  if (svg.getAttribute('width') !== null) {
    if (parseInt(svg.getAttribute('width')!, 10) < startX + stroke)
      svg.setAttribute('width', (startX + stroke).toString())
    if (parseInt(svg.getAttribute('width')!, 10) < endX + stroke)
      svg.setAttribute('width', (endX + stroke).toString())
  }

  const deltaX = (endX - startX) * 0.15
  const deltaY = (endY - startY) * 0.15
  // for further calculations which ever is the shortest distance
  // set sweep-flag (counter/clock-wise)
  // if start element is closer to the left edge,
  // draw the first arc counter-clockwise, and the second one clock-wise
  let arc1 = 0
  let arc2 = 1
  if (startX > endX) {
    arc1 = 1
    arc2 = 0
  }
  // draw tha pipe-like path
  // 1. move a bit down, 2. arch,  3. move a bit to the right, 4.arch, 5. move down to the end
  if (!isNaN(startX) && !isNaN(startY) && !isNaN(endX) && !isNaN(endY)) {
    path.setAttribute(
      'd',
      'M  ' +
        startX +
        ' ' +
        startY +
        ' L ' +
        startX +
        ' ' +
        startY +
        ' L ' +
        endX +
        ' ' +
        endY
    )
  }
}

const width = (element: HTMLElement) => {
  if (!('width' in element)) {
    return element.offsetWidth
      ? element.offsetWidth
      : element.getBoundingClientRect().width
  } else {
    return element.getBoundingClientRect().width
  }
}

const height = (element: HTMLElement) => {
  if (!('height' in element)) {
    return element.offsetHeight
      ? element.offsetHeight
      : element.getBoundingClientRect().height
  } else {
    return element.getBoundingClientRect().height
  }
}

const connectElements = (svg: HTMLElement | HTMLCanvasElement | null, path: HTMLElement | HTMLCanvasElement | null, startElem: HTMLElement | null, endElem: HTMLElement, vm: CabinLabelComponentType) => {
  const svgContainer: HTMLElement | null = vm.$refs.svgContainer
  if (!svg || !path || !startElem || !endElem || !vm) {
    return
  }
  if (svgContainer) {
    // if first element is right of the second!
    if (getOffset(startElem).left < getOffset(endElem).left) {
      const temp = startElem
      startElem = endElem
      endElem = temp
    }

    // get (top, left) corner coordinates of the svg container
    const svgTop = getOffset(svgContainer).top
    const svgLeft = getOffset(svgContainer).left

    // calculate path's start (x,y)  coords
    // we want the x coordinate to visually result in the element's mid point
    const startX = getOffset(startElem).left - svgLeft // x = left offset + 0.5*width - svg's left offset
    const startY = getOffset(startElem).top + 0.5 * height(startElem) - svgTop // y = top offset + height - svg's top offset

    // calculate path's end (x,y) coords
    const endX = getOffset(endElem).left + width(endElem) - svgLeft
    const endY = getOffset(endElem).top + 0.5 * height(endElem) - svgTop

    // call function for drawing the path
    drawPath(svg, path, startX, startY, endX, endY)
  }
}

const CabinLabelComponent = defineComponent({
  props: {
    cabin: {
      type: Object as PropType<Cabin>,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    lineTargetDimensionsChanged: {
      type: Number,
      default: null,
    },
    select: {
      type: Function,
      default: () => console.log('select not implemented'), // eslint-disable-line no-console
    },
  },
  computed: {
    label() {
      return this.cabin ? this.cabin.id : ''
    },
    idAttribute() {
      return this.cabin ? 'cabin-' + this.cabin.id : ''
    },
    color: () => {
      return $cabin
    }
  },
  watch: {
    lineTargetDimensionsChanged(newVal: number, oldVal: number) {
      if (newVal && newVal !== oldVal) {
        this.renderPath()
      }
    },
  },
  updated() {
    this.renderPath()
  },
  mounted() {
    this.renderPath()
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('resize', this.renderPath)
    }
  },
  methods: {
    selectCabin() {
      connectElements(
        this.$refs.svg,
        this.$refs.svgPath,
        this.$refs.label,
        this.getCabinElementByID(this.cabin.id),
        this
      )
      this.select(null, this.cabin.id)
    },
    renderPath() {
      const vm = this
      const tryRenderPath = () => {
        if (!vm.getCabinElementByID(vm.cabin.id)) {
          window.requestAnimationFrame(tryRenderPath)
        } else {
          connectElements(
            vm.$refs.svg,
            vm.$refs.svgPath,
            vm.$refs.label,
            vm.getCabinElementByID(vm.cabin.id),
            vm
          )
        }
      }
      tryRenderPath()
    },
    getCabinElementByID(id: string) {
      return this.$parent!.$el.querySelector(`#k${id}`)
    },
  },
  beforeUnmounted() {
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('resize', this.renderPath)
    }
  },
})

type CabinLabelComponentType = InstanceType<typeof CabinLabelComponent>

export default CabinLabelComponent
</script>

<style lang="scss" scoped>
@import '../../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.label-wrapper {
  cursor: pointer;
}

.label {
  color: $color-expinet-blue;
  border: 2px solid $color-expinet-blue;

  &:hover {
    color: $color-expinet-blue-h;
    border-color: $color-expinet-blue-h;
  }

  &.selected {
    color: $color-aida-white;
    border-color: $color-aida-green;
    background-color: $color-aida-green;
  }
}

.svgContainer {
  pointer-events: none;
  position: absolute;
  overflow: visible;
  left: 0;
  top: 0;

  svg {
    overflow: visible;
    min-width: 100px;
    min-height: 100px;

    path {
      transform: translateZ(0);
      z-index: 10;
    }
  }

svg.selected path {
    stroke: $color-aida-green;
  }
}
</style>
